import type { Appointment } from '@medplum/fhirtypes';
import {
  ActionIcon,
  Alert,
  Button,
  Group,
  Modal,
  Stack,
  Text,
  Tooltip,
} from '@mantine/core';
import { IconPencil } from '@tabler/icons-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { NoteMetadataSnapshot } from '@/schemas/note-metadata.schema';
import type { AppointmentSessionType } from '@/pages/calendar/appointmentTypeCatalog';
import type { EncounterSessionTiming } from '@/lib/encounterSessionTiming';
import {
  NoteMetadataSection,
  type NoteMetadataSectionProps,
} from './NoteMetadataSection';
import {
  ParticipantAttendanceSection,
  type ParticipantAttendanceRow,
} from './ParticipantAttendanceSection';
import type { ParticipantAttendanceFhirStatus } from '../utils/participantAttendance';
import { buildSessionMetadataSummaryText } from '../utils/buildSessionMetadataSummaryText';
import { buildPracticeLocationOptionsFromAppointment } from '../utils/patientLocationOptions';
import { useSessionMetadataUiSetController } from './SessionMetadataUiContext';

/** Publishes a read-only summary into the appointment header (completed/view mode). */
export function SessionMetadataHeaderPublisher(props: {
  metadata: NoteMetadataSnapshot;
  attendanceRows?: ParticipantAttendanceRow[];
  attendanceByPatientId?: Record<string, ParticipantAttendanceFhirStatus | undefined>;
  locationByPatientId?: Record<string, string | undefined>;
  serviceTypeOptions?: AppointmentSessionType[];
  providerName?: string;
}): null {
  const setController = useSessionMetadataUiSetController();
  const summary = useMemo(
    () =>
      buildSessionMetadataSummaryText({
        metadata: props.metadata,
        attendanceRows: props.attendanceRows,
        attendanceByPatientId: props.attendanceByPatientId,
        locationByPatientId: props.locationByPatientId,
      }),
    [
      props.metadata,
      props.attendanceRows,
      props.attendanceByPatientId,
      props.locationByPatientId,
    ],
  );

  useEffect(() => {
    if (!setController) return;
    setController({
      summaryText: summary,
      readOnly: true,
      canEditAttendance: false,
      openMetadataModal: () => {},
      openAttendanceModal: () => {},
    });
    return () => setController(null);
  }, [setController, summary]);

  return null;
}

export interface SessionMetadataWorkspaceProps
  extends Omit<NoteMetadataSectionProps, 'hideParticipantAttendance' | 'sessionLocationOptions'> {
  appointment: Appointment;
  metadata: NoteMetadataSnapshot;
  providerName?: string;
  validationError?: string;
  /**
   * When true (default), publish summary + modal openers into SessionMetadataUiContext
   * so the appointment header can render the Summary / Attended edit controls.
   */
  publishToHeader?: boolean;
  /**
   * When set, the Session Summary pencil opens this (same as header Edit → SessionSchedulerModal)
   * instead of the legacy metadata-only modal.
   */
  onEditAppointment?: () => void;
}

/**
 * Session metadata modals + optional header publication.
 * Summary UI lives in the appointment header when a SessionMetadataUiProvider is present.
 */
export function SessionMetadataWorkspace({
  appointment,
  metadata,
  onChange,
  readOnly = false,
  serviceTypeOptions = [],
  participantAttendanceRows = [],
  participantAttendanceByPatientId = {},
  savedParticipantAttendanceByPatientId = {},
  participantLocationByPatientId = {},
  participantLocationOptionsByPatientId = {},
  participantLocationErrorsByPatientId = {},
  onParticipantAttendanceChange,
  onParticipantLocationChange,
  requireResolvedAttendance = false,
  sessionTiming,
  scrubberValidationErrors = {},
  inlineFieldWarnings = {},
  providerName,
  validationError,
  publishToHeader = false,
  onEditAppointment,
}: SessionMetadataWorkspaceProps): JSX.Element {
  const [metadataOpen, setMetadataOpen] = useState(false);
  const [attendanceOpen, setAttendanceOpen] = useState(false);
  const setController = useSessionMetadataUiSetController();

  const summary = useMemo(
    () =>
      buildSessionMetadataSummaryText({
        metadata,
        attendanceRows: participantAttendanceRows,
        attendanceByPatientId: participantAttendanceByPatientId,
        locationByPatientId: participantLocationByPatientId,
        sessionTiming,
      }),
    [
      metadata,
      participantAttendanceRows,
      participantAttendanceByPatientId,
      participantLocationByPatientId,
      sessionTiming,
    ],
  );

  const sessionLocationOptions = buildPracticeLocationOptionsFromAppointment(appointment);
  const canEditAttendance = Boolean(!readOnly && onParticipantAttendanceChange);

  const handleSummaryEdit = useCallback((): void => {
    if (onEditAppointment) {
      onEditAppointment();
      return;
    }
    setMetadataOpen(true);
  }, [onEditAppointment]);

  useEffect(() => {
    if (!publishToHeader || !setController) return;
    setController({
      summaryText: summary,
      validationError,
      readOnly,
      canEditAttendance,
      openMetadataModal: handleSummaryEdit,
      openAttendanceModal: () => setAttendanceOpen(true),
    });
    return () => setController(null);
  }, [
    publishToHeader,
    setController,
    summary,
    validationError,
    readOnly,
    canEditAttendance,
    handleSummaryEdit,
  ]);

  return (
    <>
      <SessionMetadataSummaryReadonly
        appointment={appointment}
        metadata={metadata}
        attendanceRows={participantAttendanceRows}
        attendanceByPatientId={participantAttendanceByPatientId}
        locationByPatientId={participantLocationByPatientId}
        serviceTypeOptions={serviceTypeOptions}
        providerName={providerName}
        sessionTiming={sessionTiming}
        onEdit={readOnly ? undefined : handleSummaryEdit}
      />
      {validationError ? (
        <Alert color="red" variant="light" mt="xs" mb="sm" title="Required field">
          {validationError}
        </Alert>
      ) : null}

      <Modal
        opened={metadataOpen}
        onClose={() => setMetadataOpen(false)}
        title="Session metadata"
        size="lg"
        centered
      >
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            Date and start time come from the appointment, and service codes from the appointment
            service type. Edits to modality, location, and session duration sync when you save.
          </Text>
          <NoteMetadataSection
            appointment={appointment}
            metadata={metadata}
            onChange={onChange}
            serviceTypeOptions={serviceTypeOptions}
            sessionTiming={sessionTiming}
            scrubberValidationErrors={scrubberValidationErrors}
            inlineFieldWarnings={inlineFieldWarnings}
            hideParticipantAttendance
            sessionLocationOptions={sessionLocationOptions}
            readOnly={readOnly}
          />
          <Group justify="flex-end">
            <Button color="gold" onClick={() => setMetadataOpen(false)}>
              {readOnly ? 'Close' : 'Save'}
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal
        opened={attendanceOpen}
        onClose={() => setAttendanceOpen(false)}
        title="Participant attendance"
        size="lg"
        centered
      >
        <Stack gap="md">
          <ParticipantAttendanceSection
            rows={participantAttendanceRows}
            valueByPatientId={participantAttendanceByPatientId}
            savedStatusByPatientId={savedParticipantAttendanceByPatientId}
            locationByPatientId={participantLocationByPatientId}
            locationOptionsByPatientId={participantLocationOptionsByPatientId}
            locationErrorsByPatientId={participantLocationErrorsByPatientId}
            onChange={onParticipantAttendanceChange ?? (() => {})}
            onLocationChange={onParticipantLocationChange}
            readOnly={readOnly || !onParticipantAttendanceChange}
            requireResolvedAttendance={requireResolvedAttendance}
          />
          <Group justify="flex-end">
            <Button color="gold" onClick={() => setAttendanceOpen(false)}>
              Done
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}

/** Compact read-only summary for notes (first section of the note body). */
export function SessionMetadataSummaryReadonly(props: {
  appointment?: Appointment;
  metadata: NoteMetadataSnapshot;
  attendanceRows?: ParticipantAttendanceRow[];
  attendanceByPatientId?: Record<string, ParticipantAttendanceFhirStatus | undefined>;
  locationByPatientId?: Record<string, string | undefined>;
  serviceTypeOptions?: AppointmentSessionType[];
  providerName?: string;
  sessionTiming?: EncounterSessionTiming;
  onEdit?: () => void;
}): JSX.Element {
  const summary = buildSessionMetadataSummaryText({
    metadata: props.metadata,
    attendanceRows: props.attendanceRows,
    attendanceByPatientId: props.attendanceByPatientId,
    locationByPatientId: props.locationByPatientId,
    sessionTiming: props.sessionTiming,
  });

  return (
    <Stack gap={6}>
      <Group gap="xs" align="center" wrap="nowrap">
        <Text size="sm" fw={700}>
          Session Summary
        </Text>
        {props.onEdit ? (
          <Tooltip label="Edit appointment">
            <ActionIcon
              variant="subtle"
              color="gold"
              size="sm"
              aria-label="Edit appointment"
              onClick={props.onEdit}
            >
              <IconPencil size={14} />
            </ActionIcon>
          </Tooltip>
        ) : null}
      </Group>
      <Text size="sm" c="gray.8" lh={1.55}>
        {summary}
      </Text>
    </Stack>
  );
}
